import React from 'react';
import { useToasts } from '@applyboard/crystal-ui';
import { useStaticProps } from '../../context';
import { useCreateCourseMappingActivity } from './useCreateCourseMappingActivity';
import { CourseMappingDialogForm, CourseMappingFormData } from '../CourseMappingDialogForm';
import { getApiErrorMessage } from '~/utils/getApiErrorMessage';

export function AddCourseMappingDialog(props: { onClose: () => void }) {
  const { referenceData } = useStaticProps();
  const activity = useCreateCourseMappingActivity();
  const toasts = useToasts();

  // Initial empty values for create
  const initialValues: CourseMappingFormData = {
    sourceCountryCode: '',
    sourceEducationLevel: '',
    sourceCourseName: '',
    sourceInstitutionName: '',
    equivalentCourse: '',
  };

  // Prepare dropdown options
  const countries = React.useMemo(
    () =>
      Object.entries(referenceData.countries)
        .map(([code, value]) => ({
          label: value.countryLabel,
          value: code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.countries]
  );

  const educationLevels = React.useMemo(
    () =>
      Object.entries(referenceData.educationLevels)
        .map(([id, value]) => ({
          label: value.name,
          value: id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.educationLevels]
  );

  const courses = React.useMemo(
    () =>
      Object.entries(referenceData.courses)
        .map(([id, value]) => ({
          label: `${value.code} - ${value.name}`,
          value: id,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [referenceData.courses]
  );

  const handleSubmit = React.useCallback(
    (formData: CourseMappingFormData) => {
      activity
        .create({
          equivalentCourse: formData.equivalentCourse,
          sourceCourseName: formData.sourceCourseName,
          sourceCountryCode: formData.sourceCountryCode || undefined,
          sourceEducationLevel: formData.sourceEducationLevel || undefined,
          sourceInstitutionName: formData.sourceInstitutionName || undefined,
        })
        .then(() => {
          toasts.positive('Successfully created course mapping');
          props.onClose();
        })
        .catch((error) => {
          toasts.negative(
            new Error(getApiErrorMessage(error, 'Failed to create course mapping. Please try again.'), {
              cause: error,
            })
          );
        });
    },
    [activity, toasts, props]
  );

  return (
    <CourseMappingDialogForm
      dialogTitle="Add Course Mapping"
      submitButtonText="Create Mapping"
      initialValues={initialValues}
      isBusy={activity.isBusy}
      onSubmit={handleSubmit}
      onCancel={props.onClose}
      countries={countries}
      educationLevels={educationLevels}
      courses={courses}
    />
  );
}
